/**
 * @fileoverview
 * Pure adapter: maps backend NotificationEvent payload to in-app list item.
 * No HTTP, no DOM, no state mutation.
 *
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import {
  InAppNotificationItem,
  NotificationEventPayload,
} from '../models/in-app-notification.model';

function formatTime(timestamp: string | Date | undefined): string {
  if (!timestamp) return 'Now';
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString();
}

function generateId(): string {
  return `inapp_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Maps a backend notification event to the in-app list item shape.
 */
function toTimestampIso(timestamp: string | Date | undefined): string | undefined {
  if (timestamp === undefined) return undefined;
  if (typeof timestamp === 'string') return timestamp;
  return timestamp instanceof Date ? timestamp.toISOString() : undefined;
}

/**
 * Maps a backend notification event to the in-app list item shape.
 */
export function notificationEventToItem(
  payload: NotificationEventPayload,
  id?: string
): InAppNotificationItem {
  const data = payload.data ?? {};
  const categoryRaw = data.category ?? payload.type ?? 'info';
  const category = String(categoryRaw).toLowerCase();

  return {
    id: id ?? generateId(),
    message: payload.message || payload.title || '',
    time: formatTime(payload.timestamp),
    unread: true,
    category,
    moduleName: data.moduleName != null ? String(data.moduleName) : undefined,
    branch: data.branch != null ? String(data.branch) : undefined,
    region: data.region != null ? String(data.region) : undefined,
    dateRange: data.dateRange != null ? String(data.dateRange) : undefined,
    auditRoute: data.auditRoute != null ? String(data.auditRoute) : undefined,
    timestamp: toTimestampIso(payload.timestamp),
  };
}

/** REST API notification item (GET /notifications response element) */
export interface NotificationApiDto {
  id: number;
  userId: number;
  type: string;
  title: string;
  message: string;
  data?: Record<string, unknown> | null;
  read: boolean;
  createdAt: string;
}

/**
 * Maps a notification from GET /notifications API to in-app list item.
 */
export function notificationDtoToItem(dto: NotificationApiDto): InAppNotificationItem {
  const data = (dto.data ?? {}) as Record<string, unknown>;
  const categoryRaw = (data['category'] as string) ?? dto.type ?? 'info';
  const category = String(categoryRaw).toLowerCase();

  return {
    id: String(dto.id),
    message: dto.message || dto.title || '',
    time: formatTime(dto.createdAt),
    unread: !dto.read,
    category,
    moduleName: data['moduleName'] != null ? String(data['moduleName']) : undefined,
    branch: data['branch'] != null ? String(data['branch']) : undefined,
    region: data['region'] != null ? String(data['region']) : undefined,
    dateRange: data['dateRange'] != null ? String(data['dateRange']) : undefined,
    auditRoute: data['auditRoute'] != null ? String(data['auditRoute']) : undefined,
    timestamp: dto.createdAt,
  };
}
